import React, { useEffect, useState } from "react";
import {
  ResponsiveModal,
  IResponsiveModalProps,
} from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Icon } from "@UI/Components/Icon/Icon";
import { Textarea } from "@UI/Components/Textarea/Textarea";
import { RadioGroup, Radio } from "@UI/Components/Radio/Radio";
import { Button } from "@UI/Components/Button/Button";
import { Warning } from "@UI/Components/Icons/Warning";
import { useDeclineServiceProvider } from "@modules/ServiceRequest/hooks/useDeclineServiceProvider";
import { declineReasons } from "./reasons";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../../messages";
import useI18n from "@I18n/hooks/useI18n";
import { IToastProps } from "@modules/ServiceRequest/types";
interface IDeclineOfferModalProps
  extends Pick<IResponsiveModalProps, "isOpen" | "onClose"> {
  conversationId: number;
  serviceProviderName: string;
  isOfferMade: boolean;
  isCreatedBySP: boolean;
  showToast: IToastProps;
  removeCandidateFromList: (conversationId: number) => void;
  onDidNotAgree?: () => void;
}

export const DeclineOfferModal = (props: IDeclineOfferModalProps) => {
  const {
    isOpen,
    onClose,
    conversationId,
    serviceProviderName,
    isOfferMade,
    isCreatedBySP,
    showToast,
    removeCandidateFromList,
    onDidNotAgree,
  } = props;

  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  const { formatMessage } = useI18n();

  const { declineServiceProvider, isLoading, isSuccess } =
    useDeclineServiceProvider(showToast);

  useEffect(() => {
    otherReason && selectedReason !== "other" && setOtherReason("");
  }, [selectedReason]);

  useEffect(() => {
    if (isOpen && isCreatedBySP) {
      setSelectedReason("did_not_agree");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSuccess) {
      removeCandidateFromList(conversationId);
      if (selectedReason === "did_not_agree") {
        onDidNotAgree?.();
      }
      handelClose();
    }
  }, [isSuccess]);

  const decline = () => {
    declineServiceProvider({
      conversationId,
      reason: selectedReason,
      otherReason,
      serviceProviderName,
    });
  };

  const handelClose = () => {
    if (isLoading) return;
    setSelectedReason("");
    setOtherReason("");
    onClose();
  };

  const isDeclineButtonDisabled =
    !selectedReason ||
    (selectedReason === "other" && !otherReason) ||
    (selectedReason === "other" && otherReason?.length < 3);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={isLoading ? undefined : handelClose}
    >
      <VStack spacing="12px" mb="12px" alignItems="start">
        <Heading as="h4" fontSize="2xl">
          <FormattedMessage
            {...messages["serviceRequest.declineOffer.title"]}
            values={{ serviceProviderName }}
          />
        </Heading>

        <HStack
          w="full"
          spacing="8px"
          py="8px"
          px="12px"
          background="orange.100"
          borderRadius="10px"
        >
          <Icon
            cursor="pointer"
            as={Warning}
            width="16px"
            height="16px"
            color="orange.700"
          />
          <Text fontSize="sm">
            <FormattedMessage
              {...messages["serviceRequest.declineOffer.warningMessage"]}
              values={{ serviceProviderName }}
            />
          </Text>
        </HStack>

        <VStack w="full" spacing="16px" alignItems="start">
          <VStack spacing="8px" alignItems="start">
            <RadioGroup
              colorScheme="seaGreen"
              size="lg"
              display="flex"
              flexDirection="column"
              gap="8px"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e)}
            >
              {Array.from(declineReasons).map(([index, item]) => {
                if (!isOfferMade && item.reasonId === "amount_too_high")
                  return null;
                else if (isOfferMade && item.reasonId === "no_created_offer")
                  return null;
                else if (!isCreatedBySP && item.reasonId === "did_not_agree")
                  return null;

                return (
                  <React.Fragment key={index + item.reasonId}>
                    <Radio
                      gap="2px"
                      value={item.reasonId}
                    >
                      <Text fontSize="md" m="0">
                        <FormattedMessage
                          {...messages[
                            `serviceRequest.declineOffer.reasons.${item.reasonId}`
                          ]}
                        />
                      </Text>
                    </Radio>
                    {item.reasonId === "other" && selectedReason === "other" && (
                      <VStack w="full" alignItems="start">
                        <Textarea
                          ml="30px"
                          value={otherReason}
                          placeholder={formatMessage(
                            messages[
                              `serviceRequest.declineOffer.tell_us_more_about_it`
                            ]
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 255) setOtherReason(value);
                          }}
                        />
                        <Text alignSelf="flex-end" color="gray.500" fontSize="sm">
                          {otherReason?.length || 0}/255
                        </Text>
                      </VStack>
                    )}
                  </React.Fragment>
                );
              })}
            </RadioGroup>
          </VStack>

          <VStack w="full" spacing="8px">
            <Button
              w="full"
              size="lg"
              color="white"
              background={isDeclineButtonDisabled ? "red.300" : "red.700"}
              _disabled={{
                _hover: { background: "red.300", cursor: "not-allowed" },
              }}
              isLoading={isLoading}
              isDisabled={isDeclineButtonDisabled}
              onClick={decline}
            >
              <FormattedMessage
                {...messages["serviceRequest.declineOffer.declineButton.text"]}
              />
            </Button>
            <Button
              w="full"
              size="lg"
              background="gray.300"
              isDisabled={isLoading}
              onClick={handelClose}
            >
              <FormattedMessage
                {...messages["serviceRequest.declineOffer.cancelButton.text"]}
              />
            </Button>
          </VStack>
        </VStack>
      </VStack>
    </ResponsiveModal>
  );
};
